// Dashboard.js - COMPLETE FIXED
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Spinner, Table, Badge, Alert } from "react-bootstrap";
import { motion } from "framer-motion";
import { 
  FaRupeeSign, 
  FaShoppingCart, 
  FaClock, 
  FaTimesCircle, 
  FaChartLine,
  FaBuilding  // <-- ADD THIS
} from "react-icons/fa";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";

import Header from "../../component/header/header";
import Sidebar from "../../component/sidebar/sidebar";
import "./dashboard.css";

const API_BASE = `${process.env.REACT_APP_API_URL}/dashboard`;

const Dashboard = () => {
  const [stats, setStats] = useState([]);
  const [commissionData, setCommissionData] = useState([]);
  const [companyCommissionData, setCompanyCommissionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setLoading(false);
          setError("No authentication token found");
          return;
        }

        const headers = { Authorization: `Bearer ${token}` };

        // ===== STATS =====
        const statsRes = await fetch(`${API_BASE}/stats`, { headers });
        if (!statsRes.ok) throw new Error(`Stats API error: ${statsRes.status}`);
        const statsData = await statsRes.json();

        // ===== MONTHLY ADMIN COMMISSION =====
        const commissionRes = await fetch(`${API_BASE}/admin-commission`, { 
          headers 
        });
        let commissionData = [];
        if (commissionRes.ok) {
          commissionData = await commissionRes.json();
        }

        // ===== COMPANY-WISE COMMISSION =====
        const companyRes = await fetch(`${API_BASE}/admin-commission-per-company`, { 
          headers 
        });
        let companyData = [];
        if (companyRes.ok) {
          companyData = await companyRes.json();
        }

        setStats([
          {
            title: "Total Admin Income",
            value: `₹${statsData.totalCommission || 0}`,
            icon: <FaRupeeSign />,
            color: "#4CAF50"
          },
          {
            title: "Total Vendor Earnings",
            value: `₹${statsData.totalVendorEarnings || 0}`,
            icon: <FaRupeeSign />,
            color: "#2196F3"
          },
          {
            title: "Total Orders",
            value: statsData.totalOrders || 0,
            icon: <FaShoppingCart />,
            color: "#9C27B0"
          },
          {
            title: "Delivered",
            value: statsData.deliveredOrders || 0,
            icon: <FaShoppingCart />,
            color: "#4CAF50"
          },
          {
            title: "Pending",
            value: statsData.pendingOrders || 0,
            icon: <FaClock />,
            color: "#FF9800"
          },
          {
            title: "Cancelled",
            value: statsData.cancelledOrders || 0,
            icon: <FaTimesCircle />,
            color: "#f44336"
          },
        ]);

        setCommissionData(commissionData);
        setCompanyCommissionData(companyData);
        setLoading(false);
      } catch (err) {
        console.error("Dashboard error:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  if (loading) {
    return (
      <>
        <Header />
        <div className="admin-layout">
          <Sidebar />
          <main className="admin-content">
            <Container fluid>
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading dashboard...</p>
              </div>
            </Container>
          </main>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="admin-layout">
          <Sidebar />
          <main className="admin-content">
            <Container fluid>
              <Alert variant="danger">
                <Alert.Heading>Error Loading Dashboard</Alert.Heading>
                <p>{error}</p>
              </Alert>
            </Container>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="admin-layout">
        <Sidebar />
        <main className="admin-content">
          <Container fluid>
            <motion.h3
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4"
            >
              Dashboard Overview
            </motion.h3>

            {/* ===== STATS CARDS ===== */}
            <Row className="g-4">
              {stats.map((item, i) => (
                <Col xl={3} lg={4} md={6} xs={12} key={i}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Card className="stat-card" style={{ borderLeft: `4px solid ${item.color}` }}>
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-muted mb-1">{item.title}</h6>
                            <h4 className="mb-0">{item.value}</h4>
                          </div>
                          <div className="stat-icon" style={{ color: item.color }}>
                            {item.icon}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>

            {/* ===== MONTHLY ADMIN COMMISSION CHART ===== */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-5"
            >
              <Card className="chart-card p-3">
                <h5 className="mb-3">Monthly Admin Income</h5>
                {!commissionData || commissionData.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <FaChartLine size={40} className="mb-2" />
                    <p>No data available</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={commissionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(v) => `₹${Number(v).toFixed(2)}`} />
                      <Line
                        type="monotone"
                        dataKey="totalCommission"
                        stroke="#FF9800"
                        strokeWidth={3}
                        dot={{ fill: '#FF9800', r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </motion.div>

            {/* ===== COMPANY-WISE ADMIN COMMISSION ===== */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-5"
            >
              <Card className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Admin Commission Per Company</h5>
                  <Badge bg="info" pill>
                    {companyCommissionData.length} Companies
                  </Badge>
                </div>
                {!companyCommissionData || companyCommissionData.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <FaBuilding size={40} className="mb-2" />
                    <p>No data available</p>
                  </div>
                ) : (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Company</th>
                        <th className="text-end">Products Sold</th>
                        <th className="text-end">Total Sales (₹)</th>
                        <th className="text-end">Admin Commission (₹)</th>
                        <th className="text-end">Vendor Earnings (₹)</th>
                        <th className="text-end">Commission %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyCommissionData.map((item, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td><strong>{item.company}</strong></td>
                          <td className="text-end">{item.totalQty || 0}</td>
                          <td className="text-end">₹{Number(item.totalSales).toFixed(2)}</td>
                          <td className="text-end text-warning fw-bold">
                            ₹{Number(item.adminCommission).toFixed(2)}
                          </td>
                          <td className="text-end text-success">
                            ₹{Number(item.vendorEarnings).toFixed(2)}
                          </td>
                          <td className="text-end">
                            {Number(item.totalSales) > 0 
                              ? ((Number(item.adminCommission) / Number(item.totalSales)) * 100).toFixed(1)
                              : 0}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card>
            </motion.div>
          </Container>
        </main>
      </div>
    </>
  );
};

export default Dashboard;